"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { getPage, getSEOTitle, getSEODescription, getFeaturedImageUrl } from "@/lib/cms";
import type { WordPressPage } from "@/lib/cms";
import RichTextRenderer from "@/components/cms/RichTextRenderer";
import { usePageMetadata } from "@/lib/usePageMetadata";

export default function FAQPage() {
  const [pageContent, setPageContent] = useState<WordPressPage | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch WordPress content
  useEffect(() => {
    const fetchPage = async () => {
      try {
        const page = await getPage("faq");
        setPageContent(page);
      } catch (error) {
        console.error("Error fetching FAQ page from WordPress:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, []);

  // Update page metadata from WordPress
  usePageMetadata({
    title: pageContent ? getSEOTitle(pageContent) : "Frequently Asked Questions",
    description: pageContent ? getSEODescription(pageContent) : "Find answers to common questions about Pacific North North.",
    path: "faq",
    image: pageContent ? getFeaturedImageUrl(pageContent) || undefined : undefined,
  });

  return (
    <div className="bg-[#F8F5E9] min-h-screen pb-24">
      {/* Hero */}
      <motion.header
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        transition={{duration: 0.6}}
        className="text-white border-b"
        style={{
          background: "linear-gradient(to bottom, #0A3740, #0E4A52)",
          borderColor: "rgba(212, 175, 55, 0.4)",
        }}
      >
        <div className="max-w-6xl mx-auto px-6 py-14 space-y-3">
          <p
            className="text-xs uppercase tracking-[0.25em]"
            style={{color: "rgba(248, 245, 233, 0.7)"}}
          >
            Frequently Asked Questions
          </p>
          <h1
            className="text-4xl font-bold tracking-tight"
            style={{fontFamily: "var(--font-montserrat)", color: "#F8F5E9"}}
          >
            {pageContent?.title.rendered || "Frequently Asked Questions"}
          </h1>
          {pageContent?.excerpt?.rendered && (
            <div
              className="text-sm md:text-base max-w-2xl"
              style={{color: "rgba(248, 245, 233, 0.85)"}}
              dangerouslySetInnerHTML={{ __html: pageContent.excerpt.rendered }}
            />
          )}
        </div>
      </motion.header>

      <main className="max-w-6xl mx-auto px-6 mt-10">
        {loading && (
          <motion.div
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            className="bg-white rounded-2xl shadow-sm border p-6 md:p-8"
            style={{borderColor: "rgba(10, 55, 64, 0.08)"}}
          >
            <p className="text-sm text-slate-500">Loading content...</p>
          </motion.div>
        )}

        {!loading && pageContent && (
          <motion.section
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="bg-white rounded-2xl shadow-sm border p-6 md:p-8"
            style={{borderColor: "rgba(10, 55, 64, 0.08)"}}
          >
            <RichTextRenderer content={pageContent.content.rendered} />
          </motion.section>
        )}

        {!loading && !pageContent && (
          <motion.section
            initial={{opacity: 0, y: 20}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="bg-white rounded-2xl shadow-sm border p-6 md:p-8 space-y-4"
            style={{borderColor: "rgba(10, 55, 64, 0.08)"}}
          >
            <p className="text-sm text-slate-600">
              FAQ content will be available here once it&apos;s added to WordPress CMS.
            </p>
            <p className="text-sm text-slate-500">
              If you have questions, please contact us at{" "}
              <a
                href="mailto:support@pacificnorthnorth.com"
                className="underline hover:opacity-80"
                style={{color: "#0A3740"}}
              >
                support@pacificnorthnorth.com
              </a>
            </p>
          </motion.section>
        )}
      </main>
    </div>
  );
}

